<template>
  <div>
    <div v-if="loading" class="text-center my-3">
      <b-spinner />
    </div>
    <b-alert v-else-if="loadError" show variant="danger">
      Не удалось загрузить фильмографию
    </b-alert>
    <template v-else>
      <b-row class="mb-2">
        <b-col v-for="tile in summaryTiles" :key="tile.title" cols="6" md>
          <div class="person-filmography__tile">
            <div class="h4 mb-0">{{ tile.value }}</div>
            <small class="text-muted">{{ tile.title }}</small>
          </div>
        </b-col>
      </b-row>

      <div class="d-flex flex-wrap align-items-center mb-1">
        <b-button
          size="sm"
          class="mr-50 mb-50"
          :variant="professionFilter === null ? 'primary' : 'outline-primary'"
          @click="professionFilter = null"
        >
          Все {{ actualItems.length }}
        </b-button>
        <b-button
          v-for="(count, profession) in professionCounts"
          :key="profession"
          size="sm"
          class="mr-50 mb-50"
          :variant="professionFilter === profession ? 'primary' : 'outline-primary'"
          @click="professionFilter = profession"
        >
          {{ professionLabel(profession) }} {{ count }}
        </b-button>

        <b-form-checkbox
          v-model="onlyActual"
          v-b-tooltip.hover
          title="Скрыть прошедшие фильмы без голосов на КП"
          switch
          class="ml-auto mr-1 mb-50"
        >
          Актуальное
        </b-form-checkbox>
        <b-form-radio-group
          v-model="databaseFilter"
          :options="databaseFilterOptions"
          buttons
          size="sm"
          button-variant="outline-secondary"
          class="mb-50"
        />
      </div>

      <b-table
        :items="visibleItems"
        :fields="fields"
        sort-by="year"
        :sort-desc="true"
        :sort-compare="sortCompare"
        responsive
        small
        hover
        show-empty
        empty-text="Ничего не найдено"
      >
        <template #cell(poster)="{ item }">
          <b-avatar v-if="item.movie" :src="item.movie.poster" size="40" rounded />
        </template>

        <template #cell(title)="{ item }">
          <div class="font-weight-bold">
            <feather-icon v-if="item.general" v-b-tooltip.hover title="Главная роль" icon="KeyIcon" class="mr-25" />
            <b-link v-if="item.movie" :to="{ name: 'movie-view', params: { id: item.movie.id } }">
              {{ item.name_ru || item.name_en }}
            </b-link>
            <template v-else>{{ item.name_ru || item.name_en }}</template>
          </div>
          <small v-if="item.name_en && item.name_ru" class="text-muted">{{ item.name_en }}</small>
        </template>

        <template #cell(role)="{ item }">
          <div v-if="item.role">{{ item.role }}</div>
          <small v-if="professionFilter === null" class="text-muted">{{ professionLabel(item.profession) }}</small>
        </template>

        <template #cell(rating)="{ item }">
          <span
            v-if="item.rating !== null"
            class="font-weight-bold"
            :class="item.rating < 5 ? 'text-danger' : item.rating < 7 ? 'text-secondary' : 'text-success'"
          >
            {{ ratingFmt(item.rating) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(votes)="{ item }">
          {{ item.movie && item.movie.votes_kp ? formatNumber(item.movie.votes_kp) : '' }}
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex justify-content-end">
            <b-button
              v-if="item.movie"
              :to="{ name: 'movie-view', params: { id: item.movie.id } }"
              v-b-tooltip.hover
              title="Открыть в БД"
              variant="primary"
              class="btn-icon"
              size="sm"
            >
              <feather-icon icon="ArrowRightCircleIcon" />
            </b-button>
            <b-button
              v-else-if="canManage"
              v-b-tooltip.hover
              title="Добавить в БД"
              variant="success"
              class="btn-icon"
              size="sm"
              :disabled="addingKpId === item.kp_id"
              @click="addMovie(item)"
            >
              <b-spinner v-if="addingKpId === item.kp_id" small />
              <feather-icon v-else icon="PlusIcon" />
            </b-button>
            <b-button
              :href="`https://www.kinopoisk.ru/film/${item.kp_id}/`"
              target="_blank"
              v-b-tooltip.hover
              title="Кинопоиск"
              variant="warning"
              class="btn-icon ml-50"
              size="sm"
            >
              <feather-icon icon="CastIcon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BAvatar, BButton, BCol, BFormCheckbox, BFormRadioGroup, BLink, BRow, BSpinner, BTable, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatNumber, ratingFmt } from '@core/utils/filter'
import { professionLabel, professionKeysByTitle } from '@/views/database/person/professions'

export default {
  components: {
    BAlert, BAvatar, BButton, BCol, BFormCheckbox, BFormRadioGroup, BLink, BRow, BSpinner, BTable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    personId: { type: Number, required: true },
    // Профессии персоны по порядку значимости («Режиссер», «Сценарист», …) — первая открывается по умолчанию
    personProfessions: { type: Array, default: () => [] },
  },
  data() {
    return {
      filmography: { items: [], professions: {}, summary: {} },
      loading: true,
      loadError: false,
      professionFilter: null,
      onlyActual: true,
      databaseFilter: 'all',
      databaseFilterOptions: [
        { text: 'Все', value: 'all' },
        { text: 'В БД', value: 'in_db' },
        { text: 'Не в БД', value: 'not_in_db' },
      ],
      addingKpId: null,
      fields: [
        { key: 'poster', label: '' },
        { key: 'title', label: 'Название' },
        { key: 'year', label: 'Год', sortable: true },
        { key: 'role', label: 'Роль' },
        { key: 'rating', label: 'КП', sortable: true },
        { key: 'votes', label: 'Голоса', sortable: true },
        { key: 'actions', label: '', tdClass: 'text-right' },
      ],
    }
  },
  computed: {
    canManage() {
      return this.$can('manage', 'db-movies')
    },
    // «Актуальное»: убираем фильмы прошлых лет без голосов на КП. Голоса знаем только у фильмов из БД,
    // для остальных признак — рейтинг КП (его нет, пока голосов мало). Без года и без голосов — тоже скрываем
    actualItems() {
      if (!this.onlyActual) return this.filmography.items
      const currentYear = new Date().getFullYear()
      return this.filmography.items.filter(item => {
        const hasVotes = (item.movie && item.movie.votes_kp > 0) || item.rating !== null
        return hasVotes || (item.year !== null && item.year >= currentYear)
      })
    },
    professionCounts() {
      const counts = {}
      Object.keys(this.filmography.professions).forEach(profession => { counts[profession] = 0 })
      this.actualItems.forEach(item => { counts[item.profession] = (counts[item.profession] || 0) + 1 })
      return counts
    },
    visibleItems() {
      return this.actualItems.filter(item => {
        if (this.professionFilter !== null && item.profession !== this.professionFilter) return false
        if (this.databaseFilter === 'in_db') return item.movie !== null
        if (this.databaseFilter === 'not_in_db') return item.movie === null
        return true
      })
    },
    summaryTiles() {
      const { summary } = this.filmography
      return [
        { title: 'фильмов', value: summary.total || 0 },
        { title: 'в БД', value: summary.in_db || 0 },
        { title: 'с вопросами', value: summary.with_questions || 0 },
        { title: 'годы карьеры', value: summary.years ? `${summary.years[0]}–${summary.years[1]}` : '—' },
        { title: 'средний КП', value: summary.avg_rating !== null && summary.avg_rating !== undefined ? summary.avg_rating : '—' },
      ]
    },
  },
  created() {
    this.load()
  },
  methods: {
    formatNumber,
    ratingFmt,
    professionLabel,
    load() {
      this.loadError = false
      return this.$http.get(`/person/${this.personId}/filmography`)
        .then(res => {
          this.filmography = res.data
          // Вкладку профессии выбираем только при первой загрузке, перезапрос после «Добавить» её не сбрасывает
          if (this.loading) this.professionFilter = this.mainProfession()
        })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    // Основная профессия — первая из анкеты, по которой в фильмографии есть фильмы
    mainProfession() {
      const available = Object.keys(this.filmography.professions)
      const keys = this.personProfessions.flatMap(title => professionKeysByTitle(title))
      return keys.find(key => available.includes(key)) || null
    },
    // Пустые значения — всегда в конце списка, независимо от направления сортировки
    sortCompare(first, second, key, sortDesc) {
      const valueOf = item => {
        if (key === 'votes') return item.movie ? item.movie.votes_kp : null
        return item[key]
      }
      const firstValue = valueOf(first)
      const secondValue = valueOf(second)
      if (firstValue === null || firstValue === undefined) return sortDesc ? -1 : 1
      if (secondValue === null || secondValue === undefined) return sortDesc ? 1 : -1
      return firstValue - secondValue
    },
    addMovie(item) {
      this.addingKpId = item.kp_id
      this.$http.post('/movie', { kp_id: item.kp_id, title_ru: item.name_ru })
        .then(() => this.load())
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Не удалось добавить фильм', variant: 'danger', icon: 'AlertTriangleIcon' },
          })
        })
        .finally(() => { this.addingKpId = null })
    },
  },
}
</script>

<style lang="scss" scoped>
.person-filmography__tile {
  padding: 0.5rem 0.75rem;
  border-radius: 0.428rem;
  background: rgba(115, 103, 240, 0.08);
  margin-bottom: 0.5rem;
}
</style>
